package com.havn.app.features.settings.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.Canvas
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Surface
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.ui.geometry.Offset
import androidx.compose.ui.geometry.Size
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.input.PasswordVisualTransformation
import androidx.compose.ui.unit.dp
import androidx.compose.foundation.text.KeyboardOptions
import com.havn.app.core.billing.HavnBillingCustomerState
import com.havn.app.core.billing.HavnBillingPeriod
import com.havn.app.core.billing.HavnBillingService
import com.havn.app.core.billing.HavnStorePlanOption
import com.havn.app.core.billing.HavnStorePlans
import com.havn.app.core.designsystem.HavnBackground
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnCardBorder
import com.havn.app.core.designsystem.HavnDanger
import com.havn.app.core.designsystem.HavnGold
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnLeadCard
import com.havn.app.core.designsystem.HavnPropertyCard
import com.havn.app.core.designsystem.HavnPushNavigation
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.HavnSearchField
import com.havn.app.core.designsystem.HavnSpacing
import com.havn.app.core.designsystem.HavnStatCard
import com.havn.app.core.designsystem.HavnStatus
import com.havn.app.core.designsystem.HavnStatusBadge
import com.havn.app.core.designsystem.HavnSurfaceElevated
import com.havn.app.core.designsystem.HavnTextMuted
import com.havn.app.core.designsystem.HavnTextPrimary
import com.havn.app.core.designsystem.HavnTextSecondary
import com.havn.app.core.designsystem.HavnTextField
import com.havn.app.core.designsystem.havnIsWebShell
import com.havn.app.core.localization.HavnLanguage
import com.havn.app.core.localization.HavnLanguageRepository
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.media.HavnPhotoPicker
import com.havn.app.core.settings.HavnCurrency
import com.havn.app.core.settings.HavnMeasurementSystem
import com.havn.app.core.settings.HavnUnitPreferencesRepository
import com.havn.app.core.settings.HavnKioskSecurityRepository
import com.havn.app.features.agency.domain.Agency
import com.havn.app.features.agency.domain.HavnSubscriptionTier
import com.havn.app.features.agency.presentation.AgencyUiState
import com.havn.app.features.agency.presentation.AgencyViewModel
import com.havn.app.features.auth.domain.AuthRepository
import com.havn.app.features.auth.domain.AuthState
import com.havn.app.features.notifications.domain.HavnNotificationPreferences
import com.havn.app.features.notifications.domain.HavnNotificationSettings
import com.havn.app.features.notifications.domain.HavnNotificationType
import com.havn.app.features.notifications.domain.NotificationRepository
import com.havn.app.ui.theme.HavnThemeCatalog
import com.havn.app.ui.theme.HavnThemeManager
import com.havn.app.ui.theme.HavnThemeTokens
import com.havn.app.ui.theme.HavnThemeType
import com.havn.app.ui.theme.LocalHavnThemeTokens
import com.havn.app.ui.theme.canUsePremiumThemes
import kotlinx.coroutines.launch

@Composable
fun SettingsScreen(
    authRepository: AuthRepository,
    agencyViewModel: AgencyViewModel,
    languageRepository: HavnLanguageRepository,
    unitPreferencesRepository: HavnUnitPreferencesRepository,
    kioskSecurityRepository: HavnKioskSecurityRepository,
    themeManager: HavnThemeManager,
    notificationRepository: NotificationRepository,
    billingService: HavnBillingService,
    photoPicker: HavnPhotoPicker,
    openPurchaseOnStart: Boolean = false,
    onPurchaseStartConsumed: () -> Unit = {},
    onAgentProfileChanged: () -> Unit = {},
    onBack: () -> Unit = {},
    onLogout: () -> Unit = {}
) {
    val strings = LocalHavnStrings.current
    val authState by authRepository.authState.collectAsState()
    val agencyState by agencyViewModel.uiState.collectAsState()
    val selectedLanguage by languageRepository.language.collectAsState()
    val unitPreferences by unitPreferencesRepository.preferences.collectAsState()
    val kioskPin by kioskSecurityRepository.pin.collectAsState()
    val selectedTheme by themeManager.themeType.collectAsState()
    val theme = LocalHavnThemeTokens.current
    val scope = rememberCoroutineScope()
    val userId = (authState as? AuthState.LoggedIn)?.user?.id
    var firstName by remember(userId) { mutableStateOf("") }
    var lastName by remember(userId) { mutableStateOf("") }
    var avatarDataUrl by remember(userId) { mutableStateOf<String?>(null) }
    var profileError by remember(userId) { mutableStateOf<String?>(null) }
    var isSavingProfile by remember(userId) { mutableStateOf(false) }
    var isUploadingPhoto by remember(userId) { mutableStateOf(false) }
    var settingsScreen by remember { mutableStateOf(SettingsSubScreen.Main) }
    var notificationPreferences by remember(userId) { mutableStateOf(HavnNotificationPreferences()) }
    var notificationError by remember(userId) { mutableStateOf<String?>(null) }
    var showSignOutDialog by remember { mutableStateOf(false) }
    var upgradePrompt by remember { mutableStateOf<String?>(null) }
    val desktopButtonModifier = if (havnIsWebShell) Modifier.widthIn(max = 360.dp) else Modifier.fillMaxWidth()

    LaunchedEffect(userId) {
        if (userId != null) {
            authRepository.agentProfile(userId)
                .onSuccess {
                    firstName = it.firstName
                    lastName = it.lastName
                    avatarDataUrl = it.avatarDataUrl
                    profileError = null
                }
                .onFailure { profileError = it.message }
            notificationRepository.preferences(userId)
                .onSuccess {
                    notificationPreferences = it
                    notificationError = null
                }
                .onFailure { notificationError = it.message }
        }
    }

    LaunchedEffect(openPurchaseOnStart) {
        if (openPurchaseOnStart) {
            settingsScreen = SettingsSubScreen.Purchase
            onPurchaseStartConsumed()
        }
    }

    HavnPushNavigation(
        targetState = settingsScreen,
        depthFor = SettingsSubScreen::depth,
        modifier = Modifier.fillMaxSize()
    ) { visibleScreen ->
        when (visibleScreen) {
            SettingsSubScreen.Purchase -> PurchaseScreen(
                billingService = billingService,
                userId = userId,
                onPurchaseCompleted = {
                    agencyViewModel.applyRevenueCatTier(it.tier)
                    settingsScreen = SettingsSubScreen.Main
                },
                onBack = { settingsScreen = SettingsSubScreen.Main }
            )
            SettingsSubScreen.ThemeShowcase -> ThemeShowcaseScreen(onBack = { settingsScreen = SettingsSubScreen.Main })
            SettingsSubScreen.Notifications -> NotificationsSettingsScreen(
                preferences = notificationPreferences,
                error = notificationError,
                onPreferenceChanged = { type, enabled ->
                    notificationPreferences = notificationPreferences.copy(
                        enabledByType = notificationPreferences.enabledByType + (type to enabled)
                    )
                    scope.launch {
                        notificationRepository.setPreference(type, enabled)
                            .onFailure { notificationError = it.message }
                    }
                },
                onQuietHoursChanged = { settings ->
                    notificationPreferences = notificationPreferences.copy(settings = settings)
                    scope.launch {
                        notificationRepository.setQuietHours(settings)
                            .onFailure { notificationError = it.message }
                    }
                },
                onBack = { settingsScreen = SettingsSubScreen.Main }
            )
            SettingsSubScreen.CreateAgency -> CreateAgencyScreen(
                state = agencyState,
                onAgencyNameChanged = agencyViewModel::onAgencyNameChanged,
                onCreateAgency = agencyViewModel::createAgency,
                onDismissError = agencyViewModel::clearError,
                onBack = { settingsScreen = SettingsSubScreen.Main }
            )
            SettingsSubScreen.Main -> HavnScreenFrame(
                eyebrow = "",
                subtitle = "",
                title = strings.settingsTitle,
                backLabel = strings.back,
                onBack = onBack
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                    HavnCard {
                        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                            Text(strings.agentProfileTitle, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md)
                            ) {
                                Box(
                                    modifier = Modifier
                                        .size(72.dp)
                                        .clip(CircleShape)
                                        .background(theme.surface),
                                    contentAlignment = Alignment.Center
                                ) {
                                    val rendered = HavnDatabaseImage(
                                        dataUrl = avatarDataUrl,
                                        contentDescription = strings.agentPhoto,
                                        modifier = Modifier.fillMaxSize(),
                                        contentScale = androidx.compose.ui.layout.ContentScale.Crop
                                    )
                                    if (!rendered) {
                                        HavnIcon(
                                            icon = HavnIconKey.User,
                                            contentDescription = null,
                                            tint = theme.primaryAccent,
                                            modifier = Modifier.size(32.dp)
                                        )
                                    }
                                }
                                HavnButton(
                                    text = if (isUploadingPhoto) strings.uploading else strings.addPhoto,
                                    onClick = {
                                        val currentUserId = userId
                                        if (currentUserId != null) {
                                            photoPicker.pick { photo ->
                                                if (photo == null || photo.bytes.isEmpty()) {
                                                    profileError = strings.validAgentPhotoRequired
                                                    return@pick
                                                }
                                                isUploadingPhoto = true
                                                scope.launch {
                                                    authRepository.updateAgentPhoto(currentUserId, photo)
                                                        .onSuccess {
                                                            avatarDataUrl = it.avatarDataUrl
                                                            profileError = null
                                                            onAgentProfileChanged()
                                                        }
                                                        .onFailure { profileError = it.message }
                                                    isUploadingPhoto = false
                                                }
                                            }
                                        }
                                    },
                                    modifier = if (havnIsWebShell) Modifier.widthIn(max = 360.dp) else Modifier.weight(1f),
                                    style = HavnButtonStyle.Secondary,
                                    enabled = !isUploadingPhoto && userId != null,
                                    loading = isUploadingPhoto,
                                    leadingIcon = HavnIconKey.Plus
                                )
                            }
                            HavnTextField(
                                value = firstName,
                                onValueChange = {
                                    firstName = it
                                    profileError = null
                                },
                                label = strings.firstName,
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text)
                            )
                            HavnTextField(
                                value = lastName,
                                onValueChange = {
                                    lastName = it
                                    profileError = null
                                },
                                label = strings.lastName,
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text)
                            )
                            HavnButton(
                                text = if (isSavingProfile) strings.saving else strings.commonSave,
                                modifier = desktopButtonModifier,
                                enabled = !isSavingProfile && userId != null,
                                loading = isSavingProfile,
                                onClick = {
                                    val currentUserId = userId
                                    if (currentUserId != null) {
                                        isSavingProfile = true
                                        scope.launch {
                                            authRepository.updateAgentProfile(currentUserId, firstName, lastName)
                                                .onSuccess {
                                                    firstName = it.firstName
                                                    lastName = it.lastName
                                                    profileError = null
                                                    onAgentProfileChanged()
                                                }
                                                .onFailure { profileError = it.message }
                                            isSavingProfile = false
                                        }
                                    }
                                }
                            )
                            profileError?.let {
                                Text(text = it, color = HavnDanger, style = MaterialTheme.typography.labelLarge)
                            }
                        }
                    }
                    AgentPlanCard(
                        planName = agencyState.entitlement.tier.databaseValue
                            .split("_")
                            .joinToString(" ") { part -> part.replaceFirstChar { it.uppercase() } },
                        onUpgradePlan = { settingsScreen = SettingsSubScreen.Purchase },
                        onTransferPlan = { },
                        onRestorePurchase = {
                            val currentUserId = userId
                            if (currentUserId != null) {
                                scope.launch {
                                    billingService.restorePurchases(currentUserId)
                                        .onSuccess {
                                            if (it.hasActiveHavnSubscription) {
                                                agencyViewModel.applyRevenueCatTier(it.tier)
                                            } else {
                                                upgradePrompt = "No active HAVN subscription was found on this RevenueCat account."
                                            }
                                        }
                                        .onFailure { upgradePrompt = it.message }
                                }
                            }
                        }
                    )
                    AppearanceCard(
                        selectedTheme = selectedTheme,
                        canUsePremiumThemes = canUsePremiumThemes(agencyState.entitlement.tier),
                        onThemeSelected = { theme ->
                            val applied = themeManager.setTheme(
                                type = theme,
                                canUsePremiumThemes = canUsePremiumThemes(agencyState.entitlement.tier)
                            )
                            if (!applied) {
                                upgradePrompt = strings.premiumThemesUpgradePrompt
                            }
                        },
                        onPreview = { settingsScreen = SettingsSubScreen.ThemeShowcase }
                    )
                    HavnCard {
                        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                            Text(text = strings.languageTitle, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                            Text(text = strings.languageBody, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                            LanguageComboBox(
                                selectedLanguage = selectedLanguage,
                                onLanguageSelected = languageRepository::setLanguage
                            )
                        }
                    }
                    HavnCard {
                        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                            Text(text = strings.notificationsTitle, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                            Text(
                                text = strings.notificationsBody,
                                color = theme.secondaryText,
                                style = MaterialTheme.typography.bodyMedium
                            )
                            HavnButton(
                                text = strings.notificationSettings,
                                onClick = { settingsScreen = SettingsSubScreen.Notifications },
                                style = HavnButtonStyle.Secondary,
                                modifier = desktopButtonModifier,
                                leadingIcon = HavnIconKey.Alert
                            )
                            notificationError?.let {
                                Text(text = it, color = HavnDanger, style = MaterialTheme.typography.labelLarge)
                            }
                        }
                    }
                    HavnCard {
                        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                            Text(text = strings.unitsTitle, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                            Text(text = strings.unitsBody, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                            Text(text = strings.measurementSystemTitle, color = theme.mutedText, style = MaterialTheme.typography.labelLarge)
                            MeasurementSegmentedControl(
                                selectedSystem = unitPreferences.measurementSystem,
                                onSystemSelected = unitPreferencesRepository::setMeasurementSystem
                            )
                            Text(text = strings.currencyTitle, color = theme.mutedText, style = MaterialTheme.typography.labelLarge)
                            CurrencyComboBox(
                                selectedCurrency = unitPreferences.currency,
                                onCurrencySelected = unitPreferencesRepository::setCurrency
                            )
                        }
                    }
                    HavnCard {
                        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                            Text(text = strings.kioskSecurityTitle, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                            Text(text = strings.kioskSecurityBody, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                            HavnTextField(
                                value = kioskPin,
                                onValueChange = { kioskSecurityRepository.setPin(it) },
                                label = strings.kioskPinLabel,
                                visualTransformation = PasswordVisualTransformation(),
                                keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.NumberPassword)
                            )
                        }
                    }
                    HavnButton(
                        text = strings.signOut,
                        onClick = { showSignOutDialog = true },
                        modifier = desktopButtonModifier,
                        style = HavnButtonStyle.Danger,
                        leadingIcon = HavnIconKey.LogOut
                    )
                }

                if (showSignOutDialog) {
                    AlertDialog(
                        onDismissRequest = { showSignOutDialog = false },
                        containerColor = theme.card,
                        title = {
                            Text(strings.signOutQuestion, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                        },
                        text = {
                            Text(
                                strings.signOutConfirmation,
                                color = theme.secondaryText,
                                style = MaterialTheme.typography.bodyMedium
                            )
                        },
                        confirmButton = {
                            TextButton(
                                onClick = {
                                    showSignOutDialog = false
                                    onLogout()
                                }
                            ) {
                                Text(strings.signOut, color = HavnDanger)
                            }
                        },
                        dismissButton = {
                            TextButton(onClick = { showSignOutDialog = false }) {
                                Text(strings.commonCancel, color = theme.primaryAccent)
                            }
                        }
                    )
                }
                upgradePrompt?.let { message ->
                    AlertDialog(
                        onDismissRequest = { upgradePrompt = null },
                        containerColor = theme.card,
                        title = { Text(strings.premiumThemesTitle, color = theme.primaryText, style = MaterialTheme.typography.titleLarge) },
                        text = { Text(message, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium) },
                        confirmButton = {
                            TextButton(onClick = {
                                upgradePrompt = null
                            settingsScreen = SettingsSubScreen.Purchase
                        }) {
                                Text(strings.upgrade, color = theme.primaryAccent)
                            }
                        },
                        dismissButton = {
                            TextButton(onClick = { upgradePrompt = null }) {
                                Text(strings.commonCancel, color = theme.secondaryText)
                            }
                        }
                    )
                }
            }
        }
    }
}

private enum class SettingsSubScreen {
    Main,
    Purchase,
    CreateAgency,
    Notifications,
    ThemeShowcase
}

private fun SettingsSubScreen.depth(): Int = when (this) {
    SettingsSubScreen.Main -> 0
    SettingsSubScreen.Purchase,
    SettingsSubScreen.CreateAgency,
    SettingsSubScreen.Notifications,
    SettingsSubScreen.ThemeShowcase -> 1
}

@Composable
private fun NotificationsSettingsScreen(
    preferences: HavnNotificationPreferences,
    error: String?,
    onPreferenceChanged: (HavnNotificationType, Boolean) -> Unit,
    onQuietHoursChanged: (HavnNotificationSettings) -> Unit,
    onBack: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    HavnScreenFrame(
        eyebrow = "",
        title = strings.notificationsTitle,
        subtitle = strings.notificationsSubtitle,
        backLabel = strings.commonSettings,
        onBack = onBack
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                    Text(strings.quietHoursTitle, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                    Text(
                        strings.quietHoursBody,
                        color = theme.secondaryText,
                        style = MaterialTheme.typography.bodyMedium
                    )
                    SettingSwitchRow(
                        title = strings.enableQuietHours,
                        body = "${preferences.settings.quietHoursStart} to ${preferences.settings.quietHoursEnd}",
                        checked = preferences.settings.quietHoursEnabled,
                        onCheckedChange = {
                            onQuietHoursChanged(preferences.settings.copy(quietHoursEnabled = it))
                        }
                    )
                    Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                        HavnTextField(
                            value = preferences.settings.quietHoursStart,
                            onValueChange = { value ->
                                if (value.length <= 5) onQuietHoursChanged(preferences.settings.copy(quietHoursStart = normalizeTimeInput(value)))
                            },
                            label = strings.quietHoursStart,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                            modifier = Modifier.weight(1f)
                        )
                        HavnTextField(
                            value = preferences.settings.quietHoursEnd,
                            onValueChange = { value ->
                                if (value.length <= 5) onQuietHoursChanged(preferences.settings.copy(quietHoursEnd = normalizeTimeInput(value)))
                            },
                            label = strings.quietHoursEnd,
                            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
                            modifier = Modifier.weight(1f)
                        )
                    }
                    Text("${strings.timezoneLabel}: ${preferences.settings.timezone}", color = theme.mutedText, style = MaterialTheme.typography.labelMedium)
                }
            }

            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                    Text(strings.notificationTypesTitle, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                    HavnNotificationType.entries.forEach { type ->
                        SettingSwitchRow(
                            title = type.localizedTitle(strings),
                            body = type.localizedDescription(strings),
                            checked = preferences.enabled(type),
                            onCheckedChange = { enabled -> onPreferenceChanged(type, enabled) }
                        )
                    }
                }
            }
            error?.let {
                Text(text = it, color = HavnDanger, style = MaterialTheme.typography.labelLarge)
            }
        }
    }
}

@Composable
private fun SettingSwitchRow(
    title: String,
    body: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md)
    ) {
        Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(3.dp)) {
            Text(title, color = theme.primaryText, style = MaterialTheme.typography.bodyLarge)
            Text(body, color = theme.secondaryText, style = MaterialTheme.typography.bodySmall)
        }
        Switch(checked = checked, onCheckedChange = onCheckedChange)
    }
}

private fun normalizeTimeInput(value: String): String {
    val digits = value.filter { it.isDigit() }.take(4)
    if (digits.length <= 2) return digits
    return "${digits.take(2)}:${digits.drop(2)}"
}

private fun HavnNotificationType.localizedTitle(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
    HavnNotificationType.NewLeadCaptured -> strings.notificationNewLeadCapturedTitle
    HavnNotificationType.VisitorMarkedHotLead -> strings.notificationHotLeadTitle
    HavnNotificationType.OpenHouseStartsSoon -> strings.notificationOpeningStartsSoonTitle
    HavnNotificationType.OpenHouseSummaryReady -> strings.notificationOpeningSummaryReadyTitle
    HavnNotificationType.SellerReportGenerated -> strings.notificationSellerReportGeneratedTitle
    HavnNotificationType.AiFollowUpReady -> strings.notificationAiFollowUpReadyTitle
    HavnNotificationType.AgentInvitedToAgency -> strings.notificationAgentInvitedTitle
    HavnNotificationType.LeadAssigned -> strings.notificationLeadAssignedTitle
    HavnNotificationType.LeadTransferred -> strings.notificationLeadTransferredTitle
    HavnNotificationType.SubscriptionIssue -> strings.notificationSubscriptionIssueTitle
}

private fun HavnNotificationType.localizedDescription(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
    HavnNotificationType.NewLeadCaptured -> strings.notificationNewLeadCapturedBody
    HavnNotificationType.VisitorMarkedHotLead -> strings.notificationHotLeadBody
    HavnNotificationType.OpenHouseStartsSoon -> strings.notificationOpeningStartsSoonBody
    HavnNotificationType.OpenHouseSummaryReady -> strings.notificationOpeningSummaryReadyBody
    HavnNotificationType.SellerReportGenerated -> strings.notificationSellerReportGeneratedBody
    HavnNotificationType.AiFollowUpReady -> strings.notificationAiFollowUpReadyBody
    HavnNotificationType.AgentInvitedToAgency -> strings.notificationAgentInvitedBody
    HavnNotificationType.LeadAssigned -> strings.notificationLeadAssignedBody
    HavnNotificationType.LeadTransferred -> strings.notificationLeadTransferredBody
    HavnNotificationType.SubscriptionIssue -> strings.notificationSubscriptionIssueBody
}

@Composable
private fun AppearanceCard(
    selectedTheme: HavnThemeType,
    canUsePremiumThemes: Boolean,
    onThemeSelected: (HavnThemeType) -> Unit,
    onPreview: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val buttonModifier = if (havnIsWebShell) Modifier.widthIn(max = 360.dp) else Modifier.fillMaxWidth()
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            Text(strings.appearanceTitle, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            Text(
                strings.appearanceBody,
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodyMedium
            )
            HavnThemeType.entries.forEach { themeType ->
                ThemeOptionCard(
                    themeType = themeType,
                    selected = selectedTheme == themeType,
                    locked = themeType != HavnThemeType.OBSIDIAN_GOLD && !canUsePremiumThemes,
                    onClick = { onThemeSelected(themeType) }
                )
            }
            HavnButton(
                text = strings.previewComponents,
                onClick = onPreview,
                style = HavnButtonStyle.Secondary,
                modifier = buttonModifier
            )
        }
    }
}

@Composable
private fun ThemeOptionCard(
    themeType: HavnThemeType,
    selected: Boolean,
    locked: Boolean,
    onClick: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = HavnThemeCatalog.tokensFor(themeType)
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clip(MaterialTheme.shapes.medium)
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.medium,
        color = if (selected) theme.primaryAccent.copy(alpha = 0.14f) else LocalHavnThemeTokens.current.card,
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else LocalHavnThemeTokens.current.border)
    ) {
        Row(
            modifier = Modifier.padding(HavnSpacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md)
        ) {
            ThemePreviewDots(theme)
            Column(modifier = Modifier.weight(1f), verticalArrangement = Arrangement.spacedBy(3.dp)) {
                Text(themeType.localizedName(strings), color = LocalHavnThemeTokens.current.primaryText, style = MaterialTheme.typography.titleMedium)
                Text(
                    if (locked) "${themeType.localizedDescription(strings)} ${strings.proAgentPlan}" else themeType.localizedDescription(strings),
                    color = LocalHavnThemeTokens.current.secondaryText,
                    style = MaterialTheme.typography.bodySmall,
                    maxLines = 2,
                    overflow = TextOverflow.Ellipsis
                )
            }
            HavnIcon(
                icon = if (selected) HavnIconKey.Check else HavnIconKey.Star,
                contentDescription = null,
                tint = if (selected) theme.primaryAccent else LocalHavnThemeTokens.current.mutedText,
                modifier = Modifier.size(20.dp)
            )
        }
    }
}

@Composable
private fun ThemePreviewDots(theme: HavnThemeTokens) {
    Row(horizontalArrangement = Arrangement.spacedBy(5.dp)) {
        listOf(theme.background, theme.card, theme.primaryAccent, theme.secondaryAccent).forEach { color ->
            Surface(
                modifier = Modifier.size(14.dp),
                shape = CircleShape,
                color = color,
                border = BorderStroke(1.dp, theme.border)
            ) {}
        }
    }
}

@Composable
private fun ThemeShowcaseScreen(onBack: () -> Unit) {
    val strings = LocalHavnStrings.current
    HavnScreenFrame(
        eyebrow = strings.appearanceTitle,
        title = strings.themeShowcaseTitle,
        subtitle = "",
        backLabel = strings.settingsTitle,
        onBack = onBack
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                    Text(strings.showcaseButtons, color = LocalHavnThemeTokens.current.primaryText, style = MaterialTheme.typography.titleLarge)
                    HavnButton(strings.showcasePrimaryAction, onClick = {}, modifier = Modifier.fillMaxWidth())
                    HavnButton(strings.showcaseSecondaryAction, onClick = {}, style = HavnButtonStyle.Secondary, modifier = Modifier.fillMaxWidth())
                    HavnButton(strings.showcaseGhostAction, onClick = {}, style = HavnButtonStyle.Ghost, modifier = Modifier.fillMaxWidth())
                }
            }

            HavnTextField(value = "", onValueChange = {}, label = strings.showcaseTextField, placeholder = strings.showcaseFocusedBorder)
            HavnSearchField(value = "", onValueChange = {}, placeholder = strings.showcaseSearchPreview)

            Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                HavnStatCard("12", strings.navLeads, modifier = Modifier.weight(1f), icon = HavnIconKey.Leads)
                HavnStatCard("4", strings.navOpenHouses, modifier = Modifier.weight(1f), trend = "+2")
            }

            Row(horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                HavnStatusBadge(HavnStatus.Active)
                HavnStatusBadge(HavnStatus.Warm)
                HavnStatusBadge(HavnStatus.Cold)
            }

            HavnPropertyCard(
                address = "123 Maple Drive",
                city = "Miami Beach, FL",
                status = HavnStatus.Active,
                visitorCount = 24,
                hotLeadCount = 6,
                nextOpenHouseTime = "Today, 4:00 PM",
                price = "$2.49M"
            )
            HavnLeadCard(
                name = "Sophia Martinez",
                contact = "123 Maple Drive",
                sourceProperty = strings.showcaseOpeningCheckedIn,
                temperature = HavnStatus.Hot,
                lastActivityTime = strings.showcaseSentAt
            )
        }
    }
}

@Composable
private fun CreateAgencyScreen(
    state: AgencyUiState,
    onAgencyNameChanged: (String) -> Unit,
    onCreateAgency: () -> Unit,
    onDismissError: () -> Unit,
    onBack: () -> Unit
) {
    var agencyEmail by remember { mutableStateOf("") }
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val buttonModifier = if (havnIsWebShell) Modifier.widthIn(max = 360.dp) else Modifier.fillMaxWidth()

    LaunchedEffect(state.hasOwnedAgency, state.isSaving) {
        if (state.hasOwnedAgency && !state.isSaving) {
            onBack()
        }
    }

    HavnScreenFrame(
        eyebrow = "",
        title = strings.createAgency,
        subtitle = "",
        backLabel = strings.commonAgency,
        onBack = onBack
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            HavnCard {
                Column(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
                ) {
                    Surface(
                        modifier = Modifier.size(72.dp),
                        shape = CircleShape,
                        color = theme.primaryAccent.copy(alpha = 0.12f),
                        border = BorderStroke(1.dp, theme.primaryAccent)
                    ) {
                        Box(contentAlignment = Alignment.Center) {
                            HavnIcon(
                                icon = HavnIconKey.Users,
                                contentDescription = null,
                                tint = theme.primaryAccent,
                                modifier = Modifier.size(34.dp)
                            )
                        }
                    }
                    Text(
                        text = strings.createYourAgency,
                        color = theme.primaryText,
                        style = MaterialTheme.typography.titleLarge
                    )
                    Text(
                        text = strings.createAgencyBody,
                        color = theme.secondaryText,
                        style = MaterialTheme.typography.bodyMedium,
                        textAlign = androidx.compose.ui.text.style.TextAlign.Center
                    )
                }
            }

            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                    HavnTextField(
                        value = state.newAgencyName,
                        onValueChange = onAgencyNameChanged,
                        label = strings.agencyName,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Text)
                    )
                    HavnTextField(
                        value = agencyEmail,
                        onValueChange = { agencyEmail = it },
                        label = strings.agencyEmail,
                        keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Email)
                    )
                    Text(
                        text = strings.logoOptional,
                        color = theme.mutedText,
                        style = MaterialTheme.typography.labelLarge
                    )
                    Row(
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md)
                    ) {
                        Surface(
                            modifier = Modifier.size(72.dp),
                            shape = MaterialTheme.shapes.medium,
                            color = theme.background,
                            border = BorderStroke(1.dp, theme.primaryAccent.copy(alpha = 0.72f))
                        ) {
                            Box(contentAlignment = Alignment.Center) {
                                Text(
                                    text = state.newAgencyName
                                        .split(" ")
                                        .mapNotNull { it.firstOrNull()?.uppercase() }
                                        .take(2)
                                        .joinToString("")
                                        .ifBlank { "A" },
                                    color = theme.primaryAccent,
                                    style = MaterialTheme.typography.titleLarge
                                )
                            }
                        }
                        HavnButton(
                            text = strings.commonChange,
                            onClick = { },
                            style = HavnButtonStyle.Secondary
                        )
                    }
                    HavnButton(
                        text = strings.createAgency,
                        onClick = onCreateAgency,
                        loading = state.isSaving,
                        enabled = !state.isSaving,
                        modifier = buttonModifier
                    )
                }
            }

            state.error?.let { error ->
                HavnCard {
                    Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
                        Text(error, color = theme.danger, style = MaterialTheme.typography.bodyMedium)
                        HavnButton(
                            text = strings.commonDismiss,
                            onClick = onDismissError,
                            style = HavnButtonStyle.Ghost,
                            modifier = buttonModifier
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun AgentPlanCard(
    planName: String,
    onUpgradePlan: () -> Unit,
    onTransferPlan: () -> Unit,
    onRestorePurchase: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val buttonModifier = if (havnIsWebShell) Modifier.widthIn(max = 360.dp) else Modifier.fillMaxWidth()
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            Text(
                text = strings.agentPlanTitle,
                color = theme.primaryText,
                style = MaterialTheme.typography.titleLarge
            )
            Text(
                text = strings.agentPlanBody,
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodyMedium
            )
            WorkspaceOptionRow(
                title = planName,
                body = strings.currentPlan,
                selected = true,
                enabled = false,
                onClick = {}
            )
            HavnButton(
                text = strings.upgradePlan,
                onClick = onUpgradePlan,
                modifier = buttonModifier
            )
            HavnButton(
                text = strings.transferPlan,
                onClick = onTransferPlan,
                style = HavnButtonStyle.Secondary,
                modifier = buttonModifier
            )
            HavnButton(
                text = strings.restorePurchase,
                onClick = onRestorePurchase,
                style = HavnButtonStyle.Ghost,
                modifier = buttonModifier
            )
        }
    }
}

@Composable
private fun AgencySettingsCard(
    state: AgencyUiState,
    onSelectSolo: () -> Unit,
    onSelectAgency: (String) -> Unit,
    onCreateAgencyRequested: () -> Unit,
    onPurchaseRequired: () -> Unit,
    onDismissError: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val canUseAgency = state.entitlement.isProAgent
    val theme = LocalHavnThemeTokens.current
    val buttonModifier = if (havnIsWebShell) Modifier.widthIn(max = 360.dp) else Modifier.fillMaxWidth()
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            Text(
                text = strings.commonAgency,
                color = theme.primaryText,
                style = MaterialTheme.typography.titleLarge
            )
            Text(
                text = if (canUseAgency) {
                    strings.agencySettingsProBody
                } else {
                    strings.agencySettingsLockedBody
                },
                color = theme.secondaryText,
                style = MaterialTheme.typography.bodyMedium
            )

            WorkspaceOptionRow(
                title = strings.soloAgency,
                body = strings.defaultPrivateAgencyWorkspace,
                selected = state.selectedAgencyId == null,
                enabled = true,
                onClick = onSelectSolo
            )

            state.agencies.forEach { agency ->
                AgencyOptionRow(
                    agency = agency,
                    selected = state.selectedAgencyId == agency.id,
                    enabled = true,
                    canUseAgency = canUseAgency,
                    onClick = {
                        if (canUseAgency) onSelectAgency(agency.id) else onPurchaseRequired()
                    }
                )
            }

            if (state.agencies.isEmpty() && canUseAgency && !state.entitlement.canCreateAgency) {
                Text(
                    text = strings.noAgenciesAvailableYet,
                    color = theme.mutedText,
                    style = MaterialTheme.typography.bodyMedium
                )
            }

            if (state.hasOwnedAgency) {
                Text(
                    text = strings.oneOwnedAgencyHint,
                    color = theme.mutedText,
                    style = MaterialTheme.typography.bodyMedium
                )
            } else {
                HavnButton(
                    text = strings.createAgency,
                    onClick = {
                        if (!canUseAgency) {
                            onPurchaseRequired()
                        } else {
                            onCreateAgencyRequested()
                        }
                    },
                    loading = state.isSaving,
                    enabled = !state.isSaving,
                    modifier = buttonModifier
                )
            }

            state.error?.let { error ->
                Text(
                    text = error,
                    color = HavnDanger,
                    style = MaterialTheme.typography.labelLarge
                )
                HavnButton(
                    text = strings.commonDismiss,
                    onClick = onDismissError,
                    modifier = buttonModifier
                )
            }
        }
    }
}

@Composable
private fun PurchaseScreen(
    billingService: HavnBillingService,
    userId: String?,
    onPurchaseCompleted: (HavnBillingCustomerState) -> Unit,
    onBack: () -> Unit
) {
    val strings = LocalHavnStrings.current
    val scope = rememberCoroutineScope()
    var selectedPlan by remember { mutableStateOf("pro") }
    var selectedPeriod by remember { mutableStateOf(HavnBillingPeriod.Monthly) }
    var storePlans by remember { mutableStateOf(HavnStorePlans()) }
    var purchaseError by remember { mutableStateOf<String?>(null) }
    var isLoadingProducts by remember { mutableStateOf(false) }
    var isPurchasing by remember { mutableStateOf(false) }
    var isRestoring by remember { mutableStateOf(false) }
    var comparePlans by remember { mutableStateOf(false) }

    LaunchedEffect(userId) {
        val currentUserId = userId ?: return@LaunchedEffect
        isLoadingProducts = true
        billingService.loadPlans(currentUserId)
            .onSuccess {
                storePlans = it
                purchaseError = null
            }
            .onFailure { purchaseError = it.message }
        isLoadingProducts = false
    }

    fun selectedOption(): HavnStorePlanOption? {
        val tier = if (selectedPlan == "solo") HavnSubscriptionTier.SoloAgent else HavnSubscriptionTier.ProAgent
        return storePlans.option(tier, selectedPeriod)
    }

    fun priceFor(tier: HavnSubscriptionTier): String {
        return storePlans.option(tier, selectedPeriod)?.price ?: when (tier to selectedPeriod) {
            HavnSubscriptionTier.SoloAgent to HavnBillingPeriod.Monthly -> "\$9.99"
            HavnSubscriptionTier.SoloAgent to HavnBillingPeriod.Yearly -> "\$95.90"
            HavnSubscriptionTier.ProAgent to HavnBillingPeriod.Monthly -> "\$19.99"
            HavnSubscriptionTier.ProAgent to HavnBillingPeriod.Yearly -> "\$191.90"
            else -> ""
        }
    }

    fun priceLabel(tier: HavnSubscriptionTier): String {
        val periodLabel = if (selectedPeriod == HavnBillingPeriod.Monthly) strings.perMonth else strings.perYear
        return "${priceFor(tier)} $periodLabel"
    }

    HavnPushNavigation(
        targetState = comparePlans,
        depthFor = { compareVisible -> if (compareVisible) 1 else 0 },
        modifier = Modifier.fillMaxSize()
    ) { compareVisible ->
        if (compareVisible) {
            ComparePlansScreen(
                selectedPlan = selectedPlan,
                selectedPeriod = selectedPeriod,
                storePlans = storePlans,
                onSelectPlan = { selectedPlan = it },
                onBack = { comparePlans = false }
            )
        } else {
            HavnScreenFrame(
                eyebrow = "",
                title = strings.upgradeExperienceTitle,
                subtitle = strings.upgradeExperienceSubtitle,
                backLabel = strings.settingsTitle,
                onBack = onBack
            ) {
                Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
                    BillingPeriodSegmentedControl(
                        selectedPeriod = selectedPeriod,
                        onPeriodSelected = { selectedPeriod = it }
                    )
                    PlanCard(
                        title = strings.soloAgentPlan,
                        price = priceLabel(HavnSubscriptionTier.SoloAgent),
                        caption = if (selectedPeriod == HavnBillingPeriod.Yearly) strings.yearlySavings20 else null,
                        selected = selectedPlan == "solo",
                        onClick = { selectedPlan = "solo" },
                        benefits = listOf(
                            strings.soloBenefitActiveProperties,
                            strings.soloBenefitAiFollowUps,
                            strings.soloBenefitSellerReports,
                            strings.soloBenefitPersonalOnly
                        )
                    )
                    PlanCard(
                        title = strings.proAgentPlan,
                        price = priceLabel(HavnSubscriptionTier.ProAgent),
                        caption = if (selectedPeriod == HavnBillingPeriod.Yearly) strings.yearlySavings20 else null,
                        selected = selectedPlan == "pro",
                        onClick = { selectedPlan = "pro" },
                        benefits = listOf(
                            strings.proBenefitUnlimitedProperties,
                            strings.proBenefitUnlimitedAi,
                            strings.proBenefitAdvancedReports,
                            strings.proBenefitAgencyCreation,
                            strings.proBenefitInviteAgents,
                            strings.proBenefitSharedLeads,
                            strings.proBenefitAgencyBranding,
                            strings.proBenefitAdvancedAnalytics
                        )
                    )
                    HavnButton(
                        text = if (isLoadingProducts) strings.loadingPlans else strings.startFreeTrial7Day,
                        onClick = {
                            val currentUserId = userId
                            val option = selectedOption()
                            if (currentUserId == null) {
                                purchaseError = strings.signInRequiredForPurchase
                            } else if (option == null) {
                                purchaseError = strings.selectedPlanUnavailable
                            } else {
                                isPurchasing = true
                                purchaseError = null
                                scope.launch {
                                    billingService.purchase(option, currentUserId)
                                        .onSuccess { customerState ->
                                            if (customerState.hasActiveHavnSubscription) {
                                                onPurchaseCompleted(customerState)
                                            } else if (!havnIsWebShell) {
                                                purchaseError = "No active HAVN subscription was found on this RevenueCat account."
                                            }
                                        }
                                        .onFailure { purchaseError = it.message }
                                    isPurchasing = false
                                }
                            }
                        },
                        modifier = Modifier.fillMaxWidth(),
                        enabled = !isLoadingProducts && !isPurchasing,
                        loading = isPurchasing,
                        expandOnWeb = true
                    )
                    purchaseError?.let {
                        Text(text = it, color = HavnDanger, style = MaterialTheme.typography.labelLarge)
                    }
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
                    ) {
                        HavnButton(
                            text = strings.restorePurchases,
                            onClick = {
                                val currentUserId = userId
                                if (currentUserId == null) {
                                    purchaseError = strings.signInRequiredForPurchase
                                } else {
                                    isRestoring = true
                                    purchaseError = null
                                    scope.launch {
                                        billingService.restorePurchases(currentUserId)
                                            .onSuccess { customerState ->
                                                if (customerState.hasActiveHavnSubscription) {
                                                    onPurchaseCompleted(customerState)
                                                } else {
                                                    purchaseError = "No active HAVN subscription was found on this RevenueCat account."
                                                }
                                            }
                                            .onFailure { purchaseError = it.message }
                                        isRestoring = false
                                    }
                                }
                            },
                            modifier = Modifier.weight(1f),
                            style = HavnButtonStyle.Secondary,
                            enabled = !isRestoring,
                            loading = isRestoring,
                            expandOnWeb = true
                        )
                        HavnButton(
                            text = strings.comparePlans,
                            onClick = { comparePlans = true },
                            modifier = Modifier.weight(1f),
                            style = HavnButtonStyle.Secondary,
                            expandOnWeb = true
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun ComparePlansScreen(
    selectedPlan: String,
    selectedPeriod: HavnBillingPeriod,
    storePlans: HavnStorePlans,
    onSelectPlan: (String) -> Unit,
    onBack: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val buttonModifier = if (havnIsWebShell) Modifier.widthIn(max = 360.dp) else Modifier.fillMaxWidth()
    HavnScreenFrame(
        eyebrow = "",
        title = strings.comparePlans,
        subtitle = "",
        backLabel = strings.upgrade,
        onBack = onBack
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.md)) {
            PlanComparisonTable(
                selectedPlan = selectedPlan,
                selectedPeriod = selectedPeriod,
                storePlans = storePlans,
                onSelectPlan = onSelectPlan
            )
            HavnButton(
                text = strings.startFreeTrial,
                onClick = { },
                modifier = buttonModifier
            )
            Text(
                text = strings.noCommitmentCancelAnytime,
                modifier = Modifier.fillMaxWidth(),
                color = theme.mutedText,
                style = MaterialTheme.typography.bodyMedium,
                textAlign = androidx.compose.ui.text.style.TextAlign.Center
            )
        }
    }
}

@Composable
private fun PlanComparisonTable(
    selectedPlan: String,
    selectedPeriod: HavnBillingPeriod,
    storePlans: HavnStorePlans,
    onSelectPlan: (String) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    val rows = listOf(
        ComparePlanRow(HavnIconKey.Properties, strings.compareActiveProperties, "5", strings.unlimited, false),
        ComparePlanRow(HavnIconKey.Message, strings.compareAiFollowUps, strings.basic, strings.unlimited, false),
        ComparePlanRow(HavnIconKey.Reports, strings.compareSellerReports, strings.basic, strings.advanced, false),
        ComparePlanRow(HavnIconKey.Chart, strings.compareAdvancedAnalytics, "-", "", true),
        ComparePlanRow(HavnIconKey.Home, strings.compareAgencyCreation, "-", "", true),
        ComparePlanRow(HavnIconKey.Users, strings.compareJoinAgencies, "-", "", true),
        ComparePlanRow(HavnIconKey.Plus, strings.compareInviteAgents, "-", "", true),
        ComparePlanRow(HavnIconKey.Leads, strings.compareSharedLeads, "-", "", true),
        ComparePlanRow(HavnIconKey.Star, strings.compareAgencyBranding, "-", "", true),
        ComparePlanRow(HavnIconKey.Alert, strings.comparePrioritySupport, "-", "", true)
    )

    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.large,
        color = theme.card,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Column {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = HavnSpacing.sm, vertical = HavnSpacing.md),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(Modifier.weight(1.28f))
                Text(
                    text = strings.soloAgentPlan,
                    modifier = Modifier.weight(0.78f),
                    color = theme.primaryText,
                    style = MaterialTheme.typography.labelLarge,
                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                )
                Text(
                    text = strings.proAgentPlan,
                    modifier = Modifier.weight(0.78f),
                    color = theme.primaryAccent,
                    style = MaterialTheme.typography.labelLarge,
                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                )
            }
            rows.forEach { row ->
                ComparePlanRowView(row)
            }
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(HavnSpacing.sm),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Box(Modifier.weight(1.28f))
                PriceCell(
                    price = storePlans.option(HavnSubscriptionTier.SoloAgent, selectedPeriod)?.price
                        ?: if (selectedPeriod == HavnBillingPeriod.Monthly) "\$9.99" else "\$95.90",
                    period = selectedPeriod,
                    selected = selectedPlan == "solo",
                    onClick = { onSelectPlan("solo") },
                    modifier = Modifier.weight(0.78f)
                )
                PriceCell(
                    price = storePlans.option(HavnSubscriptionTier.ProAgent, selectedPeriod)?.price
                        ?: if (selectedPeriod == HavnBillingPeriod.Monthly) "\$19.99" else "\$191.90",
                    period = selectedPeriod,
                    selected = selectedPlan == "pro",
                    onClick = { onSelectPlan("pro") },
                    modifier = Modifier.weight(0.78f)
                )
            }
        }
    }
}

@Composable
private fun ComparePlanRowView(row: ComparePlanRow) {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(min = 48.dp)
            .background(theme.background.copy(alpha = 0.28f))
            .padding(horizontal = HavnSpacing.sm, vertical = HavnSpacing.sm),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Row(
            modifier = Modifier.weight(1.28f),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
        ) {
            HavnIcon(row.icon, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(16.dp))
            Text(
                text = row.feature,
                color = theme.primaryText,
                style = MaterialTheme.typography.labelLarge,
                maxLines = 2,
                overflow = TextOverflow.Ellipsis
            )
        }
        Text(
            text = row.solo,
            modifier = Modifier.weight(0.78f),
            color = theme.secondaryText,
            style = MaterialTheme.typography.labelLarge,
            textAlign = androidx.compose.ui.text.style.TextAlign.Center
        )
        Box(
            modifier = Modifier.weight(0.78f),
            contentAlignment = Alignment.Center
        ) {
            if (row.proCheck) {
                HavnIcon(HavnIconKey.Check, contentDescription = null, tint = theme.primaryAccent, modifier = Modifier.size(20.dp))
            } else {
                Text(
                    text = row.pro,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.labelLarge,
                    textAlign = androidx.compose.ui.text.style.TextAlign.Center
                )
            }
        }
    }
}

@Composable
private fun PriceCell(
    price: String,
    period: HavnBillingPeriod,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Surface(
        modifier = modifier
            .padding(horizontal = 3.dp)
            .clip(MaterialTheme.shapes.medium)
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.medium,
        color = if (selected) theme.primaryAccent.copy(alpha = 0.12f) else theme.background,
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border)
    ) {
        Column(
            modifier = Modifier.padding(vertical = HavnSpacing.sm),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(price, color = theme.primaryText, style = MaterialTheme.typography.titleMedium)
            Text(
                if (period == HavnBillingPeriod.Yearly) strings.perYear else strings.perMonth,
                color = theme.mutedText,
                style = MaterialTheme.typography.labelMedium
            )
        }
    }
}

@Composable
private fun BillingPeriodSegmentedControl(
    selectedPeriod: HavnBillingPeriod,
    onPeriodSelected: (HavnBillingPeriod) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val strings = LocalHavnStrings.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.large,
        color = theme.card,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(modifier = Modifier.padding(4.dp), horizontalArrangement = Arrangement.spacedBy(4.dp)) {
            BillingPeriodSegment(
                label = strings.monthly,
                selected = selectedPeriod == HavnBillingPeriod.Monthly,
                onClick = { onPeriodSelected(HavnBillingPeriod.Monthly) },
                modifier = Modifier.weight(1f)
            )
            BillingPeriodSegment(
                label = strings.yearlySave20,
                selected = selectedPeriod == HavnBillingPeriod.Yearly,
                onClick = { onPeriodSelected(HavnBillingPeriod.Yearly) },
                modifier = Modifier.weight(1f)
            )
        }
    }
}

@Composable
private fun BillingPeriodSegment(
    label: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier
            .clip(MaterialTheme.shapes.medium)
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.medium,
        color = if (selected) theme.primaryAccent.copy(alpha = 0.18f) else Color.Transparent,
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else Color.Transparent)
    ) {
        Box(modifier = Modifier.padding(vertical = 10.dp, horizontal = HavnSpacing.sm), contentAlignment = Alignment.Center) {
            Text(
                text = label,
                color = if (selected) theme.primaryText else theme.secondaryText,
                style = MaterialTheme.typography.labelLarge,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
    }
}

private data class ComparePlanRow(
    val icon: HavnIconKey,
    val feature: String,
    val solo: String,
    val pro: String,
    val proCheck: Boolean
)

@Composable
private fun PlanCard(
    title: String,
    price: String,
    caption: String?,
    selected: Boolean,
    onClick: () -> Unit,
    benefits: List<String>
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clip(MaterialTheme.shapes.large)
            .clickable(onClick = onClick),
        shape = MaterialTheme.shapes.large,
        color = theme.surface,
        border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border)
    ) {
        Column(
            modifier = Modifier.padding(HavnSpacing.md),
            verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.SpaceBetween
            ) {
                Column {
                    Text(title, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                    Text(price, color = theme.primaryText, style = MaterialTheme.typography.bodyLarge)
                    caption?.let {
                        Text(it, color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
                    }
                }
                Surface(
                    modifier = Modifier.size(46.dp),
                    shape = MaterialTheme.shapes.large,
                    color = if (selected) theme.primaryAccent.copy(alpha = 0.28f) else theme.background,
                    border = BorderStroke(1.dp, if (selected) theme.primaryAccent else theme.border)
                ) {
                    Box(contentAlignment = Alignment.Center) {
                        HavnIcon(
                            icon = if (selected) HavnIconKey.Star else HavnIconKey.User,
                            contentDescription = null,
                            tint = if (selected) theme.primaryAccent else theme.mutedText,
                            modifier = Modifier.size(24.dp)
                        )
                    }
                }
            }
            benefits.forEach { benefit ->
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
                ) {
                    HavnIcon(
                        icon = HavnIconKey.Check,
                        contentDescription = null,
                        tint = theme.primaryAccent,
                        modifier = Modifier.size(15.dp)
                    )
                    Text(benefit, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                }
            }
        }
    }
}

@Composable
private fun AgencyOptionRow(
    agency: Agency,
    selected: Boolean,
    enabled: Boolean,
    canUseAgency: Boolean,
    onClick: () -> Unit
) {
    val strings = LocalHavnStrings.current
    WorkspaceOptionRow(
        title = agency.name,
        body = if (canUseAgency) {
            "${agency.role.databaseValue.toSentenceCaseLabel()} - ${agency.memberCount} ${strings.members}"
        } else {
            strings.upgradeToProForAgencyTools
        },
        selected = selected,
        enabled = enabled,
        onClick = onClick
    )
}

private fun HavnThemeType.localizedName(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
    HavnThemeType.OBSIDIAN_GOLD -> strings.themeObsidianGoldName
    HavnThemeType.MIDNIGHT_SAPPHIRE -> strings.themeMidnightSapphireName
    HavnThemeType.EMERALD_ESTATE -> strings.themeEmeraldEstateName
    HavnThemeType.PLATINUM_STONE -> strings.themePlatinumStoneName
    HavnThemeType.BORDEAUX_NOIR -> strings.themeBordeauxNoirName
}

private fun HavnThemeType.localizedDescription(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
    HavnThemeType.OBSIDIAN_GOLD -> strings.themeObsidianGoldDescription
    HavnThemeType.MIDNIGHT_SAPPHIRE -> strings.themeMidnightSapphireDescription
    HavnThemeType.EMERALD_ESTATE -> strings.themeEmeraldEstateDescription
    HavnThemeType.PLATINUM_STONE -> strings.themePlatinumStoneDescription
    HavnThemeType.BORDEAUX_NOIR -> strings.themeBordeauxNoirDescription
}

@Composable
private fun WorkspaceOptionRow(
    title: String,
    body: String,
    selected: Boolean,
    enabled: Boolean,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    val borderColor = if (selected) theme.primaryAccent else theme.border
    val textColor = if (enabled) theme.primaryText else theme.mutedText
    Surface(
        modifier = Modifier
            .fillMaxWidth()
            .clip(MaterialTheme.shapes.medium)
            .clickable(enabled = enabled, onClick = onClick),
        shape = MaterialTheme.shapes.medium,
        color = if (selected) theme.primaryAccent.copy(alpha = 0.12f) else theme.card,
        border = BorderStroke(1.dp, borderColor)
    ) {
        Row(
            modifier = Modifier.padding(HavnSpacing.md),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(HavnSpacing.md)
        ) {
            Surface(
                modifier = Modifier.size(34.dp),
                shape = MaterialTheme.shapes.small,
                color = if (selected) theme.primaryAccent else theme.background,
                border = BorderStroke(1.dp, borderColor)
            ) {
                Box(contentAlignment = Alignment.Center) {
                    HavnIcon(
                        icon = if (selected) HavnIconKey.Check else HavnIconKey.Home,
                        contentDescription = null,
                        tint = if (selected) theme.background else theme.primaryAccent,
                        modifier = Modifier.size(17.dp)
                    )
                }
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = title,
                    color = textColor,
                    style = MaterialTheme.typography.bodyLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = body,
                    color = theme.mutedText,
                    style = MaterialTheme.typography.labelMedium,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
        }
    }
}

@Composable
private fun MeasurementSegmentedControl(
    selectedSystem: HavnMeasurementSystem,
    onSystemSelected: (HavnMeasurementSystem) -> Unit
) {
    val strings = LocalHavnStrings.current
    HavnSegmentedControl(
        items = listOf(
            HavnMeasurementSystem.Metric to strings.metric,
            HavnMeasurementSystem.Imperial to strings.imperial
        ),
        selectedItem = selectedSystem,
        onItemSelected = onSystemSelected
    )
}

@Composable
private fun CurrencyComboBox(
    selectedCurrency: HavnCurrency,
    onCurrencySelected: (HavnCurrency) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    val theme = LocalHavnThemeTokens.current
    Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .clickable { expanded = !expanded },
            shape = MaterialTheme.shapes.medium,
            color = theme.surface,
            border = BorderStroke(1.dp, theme.border)
        ) {
            CurrencyOptionRow(
                currency = selectedCurrency,
                trailingIcon = HavnIconKey.ArrowRight
            )
        }
        if (expanded) {
            Surface(
                modifier = Modifier.fillMaxWidth(),
                shape = MaterialTheme.shapes.medium,
                color = theme.surface,
                border = BorderStroke(1.dp, theme.border)
            ) {
                Column(modifier = Modifier.padding(vertical = HavnSpacing.xs)) {
                    HavnCurrency.entries.forEach { currency ->
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(MaterialTheme.shapes.small)
                                .clickable {
                                    expanded = false
                                    onCurrencySelected(currency)
                                }
                        ) {
                            CurrencyOptionRow(
                                currency = currency,
                                selected = currency == selectedCurrency
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun CurrencyOptionRow(
    currency: HavnCurrency,
    selected: Boolean = false,
    trailingIcon: HavnIconKey? = null
) {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(min = 54.dp)
            .background(if (selected) theme.primaryAccent.copy(alpha = 0.14f) else theme.surface)
            .padding(horizontal = HavnSpacing.md, vertical = HavnSpacing.sm),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
    ) {
        Text(
            text = currency.symbol,
            modifier = Modifier
                .size(38.dp)
                .clip(MaterialTheme.shapes.extraSmall)
                .background(theme.background.copy(alpha = 0.62f))
                .padding(vertical = HavnSpacing.sm),
            color = theme.primaryAccent,
            style = MaterialTheme.typography.labelLarge,
            maxLines = 1,
            textAlign = androidx.compose.ui.text.style.TextAlign.Center
        )
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = currency.code,
                color = theme.primaryText,
                style = MaterialTheme.typography.bodyLarge,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Text(
                text = currency.displayName,
                color = theme.mutedText,
                style = MaterialTheme.typography.labelMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
        when {
            selected -> HavnIcon(
                icon = HavnIconKey.Check,
                contentDescription = null,
                tint = theme.primaryAccent
            )
            trailingIcon != null -> HavnIcon(
                icon = trailingIcon,
                contentDescription = null,
                tint = theme.primaryAccent
            )
        }
    }
}

@Composable
private fun LanguageComboBox(
    selectedLanguage: HavnLanguage,
    onLanguageSelected: (HavnLanguage) -> Unit
) {
    var expanded by remember { mutableStateOf(false) }
    val theme = LocalHavnThemeTokens.current
    Column(verticalArrangement = Arrangement.spacedBy(HavnSpacing.sm)) {
        Surface(
            modifier = Modifier
                .fillMaxWidth()
                .clickable { expanded = !expanded },
            shape = MaterialTheme.shapes.medium,
            color = theme.surface,
            border = BorderStroke(1.dp, theme.border)
        ) {
            LanguageOptionRow(
                language = selectedLanguage,
                trailingIcon = HavnIconKey.ArrowRight
            )
        }
        if (expanded) {
            Surface(
                modifier = Modifier.fillMaxWidth(),
                shape = MaterialTheme.shapes.medium,
                color = theme.surface,
                border = BorderStroke(1.dp, theme.border)
            ) {
                Column(modifier = Modifier.padding(vertical = HavnSpacing.xs)) {
                    HavnLanguage.entries.forEach { language ->
                        Box(
                            modifier = Modifier
                                .fillMaxWidth()
                                .clip(MaterialTheme.shapes.small)
                                .clickable {
                                    expanded = false
                                    onLanguageSelected(language)
                                }
                        ) {
                            LanguageOptionRow(
                                language = language,
                                selected = language == selectedLanguage
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun LanguageOptionRow(
    language: HavnLanguage,
    selected: Boolean = false,
    trailingIcon: HavnIconKey? = null
) {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .heightIn(min = 54.dp)
            .background(if (selected) theme.primaryAccent.copy(alpha = 0.14f) else theme.surface)
            .padding(horizontal = HavnSpacing.md, vertical = HavnSpacing.sm),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(HavnSpacing.sm)
    ) {
        LanguageFlag(language)
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = language.nativeName,
                color = theme.primaryText,
                style = MaterialTheme.typography.bodyLarge,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            Text(
                text = language.displayName,
                color = theme.mutedText,
                style = MaterialTheme.typography.labelMedium,
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
        }
        when {
            selected -> HavnIcon(
                icon = HavnIconKey.Check,
                contentDescription = null,
                tint = theme.primaryAccent
            )
            trailingIcon != null -> HavnIcon(
                icon = trailingIcon,
                contentDescription = null,
                tint = theme.primaryAccent
            )
        }
    }
}

@Composable
private fun LanguageFlag(language: HavnLanguage) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.size(width = 38.dp, height = 26.dp),
        shape = MaterialTheme.shapes.extraSmall,
        color = theme.surface,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Canvas(Modifier.fillMaxSize()) {
            fun horizontal(colors: List<Color>) {
                val stripeHeight = size.height / colors.size
                colors.forEachIndexed { index, color ->
                    drawRect(
                        color = color,
                        topLeft = Offset(0f, stripeHeight * index),
                        size = Size(size.width, stripeHeight + 0.5f)
                    )
                }
            }

            fun vertical(colors: List<Color>) {
                val stripeWidth = size.width / colors.size
                colors.forEachIndexed { index, color ->
                    drawRect(
                        color = color,
                        topLeft = Offset(stripeWidth * index, 0f),
                        size = Size(stripeWidth + 0.5f, size.height)
                    )
                }
            }

            when (language) {
                HavnLanguage.English -> {
                    horizontal(
                        listOf(
                            Color(0xFFB22234),
                            Color.White,
                            Color(0xFFB22234),
                            Color.White,
                            Color(0xFFB22234),
                            Color.White,
                            Color(0xFFB22234)
                        )
                    )
                    drawRect(Color(0xFF3C3B6E), size = Size(size.width * 0.45f, size.height * 0.54f))
                }
                HavnLanguage.Slovak -> {
                    horizontal(listOf(Color.White, Color(0xFF0B4EA2), Color(0xFFEE1C25)))
                    drawCircle(Color(0xFFEE1C25), radius = size.height * 0.18f, center = Offset(size.width * 0.32f, size.height * 0.5f))
                }
                HavnLanguage.German -> horizontal(listOf(Color(0xFF111111), Color(0xFFDD0000), Color(0xFFFFCE00)))
                HavnLanguage.French -> vertical(listOf(Color(0xFF0055A4), Color.White, Color(0xFFEF4135)))
                HavnLanguage.Czech -> {
                    horizontal(listOf(Color.White, Color(0xFFD7141A)))
                    drawPath(
                        path = androidx.compose.ui.graphics.Path().apply {
                            moveTo(0f, 0f)
                            lineTo(size.width * 0.48f, size.height * 0.5f)
                            lineTo(0f, size.height)
                            close()
                        },
                        color = Color(0xFF11457E)
                    )
                }
                HavnLanguage.Spanish -> horizontal(listOf(Color(0xFFC60B1E), Color(0xFFFFC400), Color(0xFFFFC400), Color(0xFFC60B1E)))
                HavnLanguage.Italian -> vertical(listOf(Color(0xFF009246), Color.White, Color(0xFFCE2B37)))
            }
        }
    }
}

@Composable
private fun <T> HavnSegmentedControl(
    items: List<Pair<T, String>>,
    selectedItem: T,
    onItemSelected: (T) -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = MaterialTheme.shapes.medium,
        color = theme.surface,
        border = BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .background(theme.surface)
                .padding(3.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            items.forEachIndexed { index, item ->
                val (value, label) = item
                val selected = selectedItem == value
                val shape = when (index) {
                    0 -> MaterialTheme.shapes.small
                    items.lastIndex -> MaterialTheme.shapes.small
                    else -> MaterialTheme.shapes.extraSmall
                }
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .heightIn(min = 46.dp)
                        .clip(shape)
                        .background(if (selected) theme.primaryAccent else theme.surface)
                        .clickable { onItemSelected(value) },
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = label,
                        color = if (selected) theme.background else theme.primaryText,
                        style = MaterialTheme.typography.labelLarge,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
            }
        }
    }
}

private fun String.toSentenceCaseLabel(): String =
    replace("_", " ")
        .lowercase()
        .replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }
